// src/components/calculators/CalculatorHero.js
import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, CheckIcon } from "@heroicons/react/24/outline";

const CalculatorHero = ({ options, onSelect }) => {
  const styles = {
    container: {
      fontFamily: 'var(--font-family-primary)'
    },
    // FIX: Added a new style for the header card
    headerCard: {
      background: 'var(--brand-gradient-1)',
      padding: 'var(--spacing-xl)',
      borderRadius: 'var(--radius-xl)',
      boxShadow: 'var(--shadow-frame-big)',
      marginBottom: 'var(--spacing-l)',
    },
    title: {
      fontSize: 'var(--font-size-h4)',
      fontWeight: 'var(--font-weight-h4)',
      color: 'white',
      marginBottom: 'var(--spacing-s)',
      lineHeight: 'var(--line-height-h4)',
      margin: 0
    },
    subtitle: {
      fontSize: 'var(--font-size-body3)',
      color: 'var(--Text-Contrast)',
      margin: '0 auto',
      lineHeight: 'var(--line-height-body3)',
      marginTop: 'var(--spacing-xxs)',
      opacity: 0.8
    },
    card: {
      background: 'var(--Surface-Primary)',
      border: '1px solid var(--Stroke-Light)',
      borderRadius: 'var(--radius-m)',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      overflow: 'hidden',
      position: 'relative',
      animation: 'fadeIn 0.4s ease'
    },
    cardHeader: {
      height: '3px',
      transition: 'height 0.3s ease'
    },
    cardBody: {
      padding: 'var(--spacing-l)'
    },
    iconWrapper: {
      padding: 'var(--spacing-s)',
      borderRadius: 'var(--radius-s)',
      color: 'white',
      boxShadow: 'var(--shadow-md)',
      transition: 'transform 0.3s ease',
      width: 'fit-content'
    },
    cardTitle: {
      fontSize: 'var(--font-size-h6)',
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--Text-Primary)',
      marginBottom: 'var(--spacing-xs)'
    },
    cardDescription: {
      fontSize: 'var(--font-size-body4)',
      color: 'var(--Text-Secondary)',
      lineHeight: 'var(--line-height-body4)'
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--spacing-s)'
    },
    checkIcon: {
      padding: '4px',
      background: 'var(--brand-gradient-4)',
      borderRadius: 'var(--radius-pill)',
      flexShrink: 0
    },
    featureText: {
      fontSize: 'var(--font-size-body4)',
      color: 'var(--Text-Secondary)'
    },
    ctaSection: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingTop: 'var(--spacing-s)',
      borderTop: '1px solid var(--Stroke-Light)'
    },
    ctaText: {
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--Text-Primary)',
      fontSize: 'var(--font-size-body3)'
    },
    arrowIcon: {
      width: '20px',
      height: '20px',
      color: 'var(--Grey-400)',
      transition: 'all 0.3s ease'
    },
    walkthroughRow: {
      marginTop: 'var(--spacing-s)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 'var(--spacing-xs)'
    },
    walkthroughButton: {
      background: 'rgba(255, 255, 255, 0.18)',
      border: '1px solid rgba(255, 255, 255, 0.35)',
      padding: '6px 12px',
      borderRadius: '999px',
      color: '#fff',
      fontSize: 'var(--font-size-body4)',
      fontWeight: 'var(--font-weight-medium)',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 'var(--spacing-xxs)',
      backdropFilter: 'blur(6px)'
    },
    walkthroughIcon: {
      width: '1rem',
      height: '1rem'
    },
    videoModalOverlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(9, 11, 15, 0.55)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 'var(--spacing-l)'
    },
    videoModal: {
      width: 'min(1100px, 92vw)',
      maxHeight: '85vh',
      background: 'var(--Surface-Primary)',
      borderRadius: 'var(--radius-xl)',
      boxShadow: 'var(--shadow-lg)',
      border: 'var(--card-border)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    },
    videoModalHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: 'var(--spacing-l)',
      borderBottom: 'var(--card-border)'
    },
    videoModalTitle: {
      fontSize: 'var(--font-size-body2)',
      fontWeight: 'var(--font-weight-medium)',
      color: 'var(--Text-Primary)'
    },
    videoModalClose: {
      background: 'transparent',
      border: 'none',
      color: 'var(--Text-Secondary)',
      cursor: 'pointer',
      fontSize: '1.2rem'
    },
    videoFrame: {
      position: 'relative',
      aspectRatio: '16 / 9',
      width: '100%',
      flex: 1,
      minHeight: 0,
      background: 'var(--Grey-900)'
},
  };

  const [isVideoOpen, setIsVideoOpen] = useState(false);

  useEffect(() => {
    if (!isVideoOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsVideoOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVideoOpen]);

  const walkthrough = {
    title: 'How to use the calculators',
    embedUrl: 'https://player.vimeo.com/video/1170343163?badge=0&autopause=0&player_id=0&app_id=58479'
  };

  // Add animation keyframes
  const styleSheet = `
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;

  return (
    <>
      <style>{styleSheet}</style>
      <div style={styles.container}>
        {/* Hero Banner - FIX: Now wrapped in a styled card */}
        <div style={styles.headerCard}>
          <h2 style={styles.title}>
            What would you like to calculate?
          </h2>
          <div style={styles.walkthroughRow}>
            <button
              type="button"
              style={styles.walkthroughButton}
              onClick={() => setIsVideoOpen(true)}
            >
              <svg
                style={styles.walkthroughIcon}
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M8 5.14v13.72a1 1 0 0 0 1.55.83l9.2-6.86a1 1 0 0 0 0-1.66l-9.2-6.86A1 1 0 0 0 8 5.14z" />
              </svg>
              How to use the calculators
            </button>
          </div>
        </div>

        {isVideoOpen && (
          <div
            style={styles.videoModalOverlay}
            onClick={() => setIsVideoOpen(false)}
          >
            <div
              style={styles.videoModal}
              onClick={(event) => event.stopPropagation()}
            >
              <div style={styles.videoModalHeader}>
                <div style={styles.videoModalTitle}>
                  {walkthrough.title || 'How to use the calculators'}
                </div>
                <button
                  type="button"
                  style={styles.videoModalClose}
                  onClick={() => setIsVideoOpen(false)}
                  aria-label="Close video"
                >
                  ✕
                </button>
              </div>

              <div style={styles.videoFrame}>
                <iframe
                  src={walkthrough.embedUrl}
                  title={walkthrough.title || 'How to use the calculators'}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Calculator Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 'var(--spacing-l)',
          marginBottom: 'var(--spacing-xxl)'
        }}>
          {options.map((option, index) => (
            <div
              key={option.id}
              style={{
                ...styles.card,
                animationDelay: `${index * 100}ms`
              }}
              onClick={() => onSelect(option.id)}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = 'var(--shadow-xl)';
                e.currentTarget.style.transform = 'translateY(-4px)';
                const header = e.currentTarget.querySelector('.card-header');
                if (header) header.style.height = '6px';
                const icon = e.currentTarget.querySelector('.icon-wrapper');
                if (icon) icon.style.transform = 'scale(1.1)';
                const arrow = e.currentTarget.querySelector('.arrow-icon');
                if (arrow) {
                  arrow.style.color = 'var(--Text-Primary)';
                  arrow.style.transform = 'translateX(4px)';
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
                const header = e.currentTarget.querySelector('.card-header');
                if (header) header.style.height = '3px';
                const icon = e.currentTarget.querySelector('.icon-wrapper');
                if (icon) icon.style.transform = 'scale(1)';
                const arrow = e.currentTarget.querySelector('.arrow-icon');
                if (arrow) {
                  arrow.style.color = 'var(--Grey-400)';
                  arrow.style.transform = 'translateX(0)';
                }
              }}
            >
              {/* Gradient Header */}
              <div
                className="card-header"
                style={{
                  ...styles.cardHeader,
                  // FIX: Simplified the logic to reliably split colors between the two cards
                  background: index === 0
                    ? 'var(--Blue-500)'
                    : 'var(--Red-500)'
                }}
              />

              <div style={styles.cardBody}>
                {/* Icon and Title */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-m)', marginBottom: 'var(--spacing-m)' }}>
                  <div
                    className="icon-wrapper"
                    style={{
                      ...styles.iconWrapper,
                       // FIX: Simplified the logic here as well for consistency
                      background: index === 0
                        ? 'var(--Blue-500)'
                        : 'var(--Red-500)'
                    }}
                  >
                    <option.icon style={{ height: '24px', width: '24px' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={styles.cardTitle}>
                      {option.title}
                    </h3>
                    <p style={styles.cardDescription}>
                      {option.description}
                    </p>
                  </div>
                </div>

                {/* Features List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-s)', marginBottom: 'var(--spacing-m)' }}>
                  {option.features.map((feature, idx) => (
                    <div key={idx} style={styles.featureItem}>
                      <div style={styles.checkIcon}>
                        <CheckIcon style={{ height: '12px', width: '12px', color: 'var(--Grey-900)' }} />
                      </div>
                      <p style={styles.featureText}>
                        {feature}
                      </p>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <div style={styles.ctaSection}>
                  <span style={styles.ctaText}>
                    Get Started
                  </span>
                  <ArrowRightIcon className="arrow-icon" style={styles.arrowIcon} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Info Section */}
        <div style={{
          background: 'var(--brand-gradient-3)',
          borderRadius: 'var(--radius-m)',
          padding: 'var(--spacing-m)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--spacing-s)'
        }}>
          <span style={{ fontSize: '20px', marginTop: '2px' }}>💡</span>
          <div>
            <p style={{
              fontSize: 'var(--font-size-body4)',
              color: 'var(--Grey-800)',
              margin: 0,
              marginTop: 'var(--spacing-xxs)'
            }}>
              Use <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>Quick Fee Check</span> when responding to customer inquiries.
              Use <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>Quote Builder</span> when preparing formal quotes or
              planning your pricing strategy.
            </p>
             <p style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Grey-900)', lineHeight: '2'  }}>
              • Card fees are a flat 1.8% per transaction, no matter the sale size.
            </p>
            <p style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Grey-900)', lineHeight: '2'  }}>
              • You can also find a detailed breakdown of our commission model in the documents page.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CalculatorHero;